import { useEffect, useState, useCallback } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Warns the user before navigating away when there are unsaved changes.
 * Registers a browser beforeunload handler and uses react-router's useBlocker.
 *
 * Returns { showPrompt, confirmLeave, cancelLeave } so the consuming component
 * can render its own confirmation dialog (e.g. ConfirmDialog).
 *
 * @param {boolean} isDirty - Whether the form has unsaved changes
 * @param {string} [message] - Custom warning message
 */
const useUnsavedChanges = (isDirty, message = 'You have unsaved changes. Are you sure you want to leave?') => {
    const [showPrompt, setShowPrompt] = useState(false);

    // Browser tab close / refresh protection
    useEffect(() => {
        if (!isDirty) return;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = message;
            return message;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty, message]);

    // React Router in-app navigation protection
    const blocker = useBlocker(isDirty);

    useEffect(() => {
        if (blocker.state === 'blocked') {
            setShowPrompt(true);
        }
    }, [blocker.state]);

    const confirmLeave = useCallback(() => {
        setShowPrompt(false);
        if (blocker.state === 'blocked') {
            blocker.proceed();
        }
    }, [blocker]);

    const cancelLeave = useCallback(() => {
        setShowPrompt(false);
        if (blocker.state === 'blocked') {
            blocker.reset();
        }
    }, [blocker]);

    return { showPrompt, confirmLeave, cancelLeave, message };
};

export default useUnsavedChanges;
